<template>
  <SidebarLayout
    class="projects-page"
    :classname="'section-projects' + (!projects.length && !loading ? ' empty' : '')"
  >
    <div class="top">
      <div class="spacex-12 vertical-centering">
        <SidebarOpener />
        <div class="text-bs grey-900">
          {{ $t('Projects') }}
        </div>
        <Button
          v-if="canI('createProjects')"
          size="small"
          left-icon="plus"
          type="borderless"
          @click="newProject"
        >
          {{ $t('Create project') }}
        </Button>
      </div>
      <FilterTabs
        :value="currentStage"
        :tabs="stageFilterTabs"
        query="stage"
        @change="handleStageChanging"
      />
    </div>

    <div ref="scroll">
      <ScrollPagination v-model="projectsList" class="main spacey-12" :url="nextUrl">
        <ProjectCard
          v-for="project in projectsList"
          :key="project.id + project.updated_at"
          :project="project"
          @update="fetchProjectsList(false)"
        />
        <TenderCardSkeleton v-if="loading" />
        <ProjectsEmpty v-else-if="!projectsList.length" @new="newProject" />
      </ScrollPagination>
    </div>
  </SidebarLayout>
</template>

<script>
  import { mapActions, mapState } from 'vuex';
  import ProjectsEmpty from '@/components/projects/Empty';
  import SidebarOpener from '@/containers/top-menu/SidebarOpener';
  import SidebarLayout from '@/layouts/SidebarLayout';
  import AddProjectModal from '@/containers/projects/AddModal';
  import canI from '@/utils/roles/access';
  import factory from '@/utils/api/factory';
  import ProjectCard from '@/containers/projects/ProjectCard';
  import Button from '@/components/ui/Button';
  import FilterTabs from '@/components/ui/FilterTabs';
  import ScrollPagination from '@/containers/common/ScrollPagination';
  import TenderCardSkeleton from '@/skeletons/project/tenders/TenderCardSkeleton';
  import getOptimalLimit from '@/utils/api/optimal-limit';
  import project from '@/store/project';
  import { addModule } from '@/store';

  export default {
    meta: {
      title: 'Projects',
    },
    components: {
      SidebarLayout,
      SidebarOpener,
      ProjectsEmpty,
      ProjectCard,
      Button,
      ScrollPagination,
      FilterTabs,
      TenderCardSkeleton,
    },
    created() {
      addModule('project', project);
    },
    data() {
      return {
        isModalOpen: false,
        projects: [],
        projectsList: [],
        loading: false,
        currentStage: 'active',
        nextUrl: null,
      };
    },
    computed: {
      ...mapState({
        user: (state) => state.auth.user,
        modalAction: (state) => state.modal.modalAction,
        activeCompany: (state) => state.auth.user?.membership,
      }),
      stageFilterTabs() {
        return [
          {
            title: this.$t('Active'),
            slug: 'active',
          },
          {
            title: this.$t('Archived'),
            slug: 'archived',
          },
        ];
      },
    },
    watch: {
      async modalAction(newAction) {
        if (newAction.type === 'projectAdded' || newAction.type === 'projectsUpdated') {
          if (newAction?.projectId) {
            await this.$router.push({
              name: newAction?.importNeeded ? 'import' : 'summary',
              params: { id: newAction.projectId },
            });
          } else await this.fetchProjectsList();
        }
      },
    },
    async mounted() {
      if (this.$route.query.stage) {
        this.currentStage = this.$route.query.stage;
      }
      await this.fetchProjectsList(true, false);
    },
    methods: {
      ...mapActions({
        openModal: 'modal/openModal',
        changeCompanies: 'auth/changeField',
        updateSidebarProjects: 'sidebarProjects/loadSidebarProjectsList',
      }),
      canI,
      newProject() {
        this.openModal({
          component: AddProjectModal,
          context: {
            attributes: {
              class: 'new-project-modal',
            },
          },
        });
      },
      async fetchProjectsList(needLoading = true, needUpdateSidebarProjects = true) {
        if (needLoading) {
          this.loading = true;
        }
        try {
          if (needLoading) {
            this.projectsList = [];
          }
          const { data } = await factory('projects').index({
            is_archived: this.currentStage === 'archived',
            limit: getOptimalLimit(this.$refs.scroll.clientHeight, 75, 12),
            sort: '-updated_at',
          });
          if (needUpdateSidebarProjects) {
            await this.updateSidebarProjects();
          }
          this.projectsList = data.results;
          this.nextUrl = data.next;
        } catch (e) {
          console.error(e);
        }
        if (needLoading) {
          this.loading = false;
        }
      },
      handleStageChanging(slug) {
        this.currentStage = slug;
        this.fetchProjectsList();
      },
    },
  };
</script>
